import { useRef, useEffect, useState } from 'react'
import { gradientsDefaults } from '../config/gradients'

const gradientConfigs: Record<
  string,
  { angle: number; color1: string; color2: string; x?: number; y?: number }
> = gradientsDefaults

export default function GradientBackground({
  path = '/',
  children
}: {
  path?: string
  children: React.ReactNode
}) {
  const [isVisible, setIsVisible] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  const getGradientPath = (path: string): string => {
    if (path.match(/^\/explore\/[^/]+$/)) return '/tour-detail'
    if (path.match(/^\/explore\/[^/]+\/navigate$/)) return '/tour-navigation'
    if (path.match(/^\/explore\/[^/]+\/checkout$/)) return '/tour-checkout'
    return path
  }

  const findMatchingGradient = (currentPath: string) => {
    const gradientPath = getGradientPath(currentPath)
    if (gradientConfigs[gradientPath]) return gradientConfigs[gradientPath]
    const segments = gradientPath.split('/').filter(Boolean)
    while (segments.length > 0) {
      segments.pop()
      const parentPath = '/' + segments.join('/')
      if (gradientConfigs[parentPath]) return gradientConfigs[parentPath]
    }
    return gradientConfigs['/']
  }

  const { angle, color1, color2, x, y } = findMatchingGradient(path)

  useEffect(() => {
    if (containerRef.current) {
      const el = containerRef.current
      // Use CSS transitions for smooth color/angle changes
      el.style.setProperty('--conic-angle', `${angle}deg`)
      el.style.setProperty('--conic-color1', color1)
      el.style.setProperty('--conic-color2', color2)
      el.style.setProperty('--conic-pos-x', `${x ?? 50}%`)
      el.style.setProperty('--conic-pos-y', `${y ?? 0}px`)

      // Initial fade in
      setIsVisible(true)
    }
  }, [angle, color1, color2, x, y])

  return (
    <div
      ref={containerRef}
      className={`bg-conic-animated ${isVisible ? 'visible' : ''}`}
      style={{
        willChange: 'opacity, --conic-angle, --conic-color1, --conic-color2, --conic-pos-x, --conic-pos-y',
        transition: `
          opacity 0.4s ease, 
          --conic-angle 0.8s cubic-bezier(0.4, 0, 0.2, 1), 
          --conic-color1 0.8s cubic-bezier(0.4, 0, 0.2, 1), 
          --conic-color2 0.8s cubic-bezier(0.4, 0, 0.2, 1), 
          --conic-pos-x 0.8s cubic-bezier(0.4, 0, 0.2, 1), 
          --conic-pos-y 0.8s cubic-bezier(0.4, 0, 0.2, 1)
        `
      } as React.CSSProperties}
    >
      {children}
    </div>
  )
}
